import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Card from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { ICarrierState } from 'types/carrier'
import { IHighwayVehicle, IHighwayVehicleTypes } from 'types/graphqlTypes'
import { IStore } from 'apps/carrier/utils/reducers'
import { Pagination, brandColors } from 'transfix-ui'
import { DataReportedBy } from '../../DataReportedBy'
import { LoadableValue } from '../../LoadableValue'
import { VinItem } from './VinItem'
import { useVinsQuery } from './graphql/Vins'

const ITEMS_PER_PAGE = 16

export const Vins = () => {
  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierApplicationId = '' } = carrierDetails || {}
  const { data, error, loading, refetch } = useVinsQuery({
    variables: {
      carrierApplicationId,
      after: null,
      before: null,
      first: ITEMS_PER_PAGE,
      type: IHighwayVehicleTypes.power_unit,
    },
  })

  const { highwayCarrier } = data || {}
  const { vehicles: powerUnitVehicles } = highwayCarrier || {}
  const { nodes, totalCount } = powerUnitVehicles || {}
  const vehicles = nodes?.filter(vehicle => !!vehicle) as IHighwayVehicle[]

  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)

  const pageChangeHandler = (newPageNumber: number) => {
    /**
     * The connection strategy that the backend uses base 64 encryption to generate the cursors like "MTA" and "MQ"
     * so taking advantage of this, we can create the cursor to send to the BE
     */
    const cursor = btoa(`${newPageNumber * ITEMS_PER_PAGE - ITEMS_PER_PAGE}`)
    refetch({
      after: cursor,
      before: null,
      first: ITEMS_PER_PAGE,
      type: IHighwayVehicleTypes.power_unit,
    })
    setCurrentPageNumber(newPageNumber)
  }

  const shouldShowPagination = !!totalCount

  return (
    <Card>
      <Typography variant='h3'>Vins</Typography>
      <DataReportedBy reporter='highway' />

      <Box
        mt={4}
        height='56px'
        display='grid'
        alignItems='center'
        gridTemplateColumns='1fr 160px'
        borderBottom={`1px solid ${brandColors.coolGray3}`}>
        <Typography component='span' color='textSecondary' variant='body2'>
          VIN Number
        </Typography>
        <Typography component='span' color='textSecondary' variant='body2'>
          On Schedule
        </Typography>
      </Box>

      {error && <InlineMessage message={error.message} type={INLINE_MESSAGE_VARIANTS.ALERT} />}

      {!loading && !vehicles?.length && (
        <Box display='grid' alignItems='center' height='56px'>
          <Typography variant='body2'>No VINs found</Typography>
        </Box>
      )}
      {!loading && vehicles?.map(({ id, vin }) => <VinItem key={id} vin={vin} />)}
      {loading && (
        <Box
          mt={4}
          display='grid'
          alignItems='center'
          gridTemplateColumns='1fr 160px'
          borderBottom={`1px solid ${brandColors.coolGray3}`}>
          <LoadableValue loading value='' />
          <LoadableValue loading value='' />
        </Box>
      )}
      {shouldShowPagination && (
        <Box display='grid' justifyContent='center' mt='24px'>
          <Pagination
            currentPage={currentPageNumber}
            numberOfPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
            displayPageNumbers
            onPageChange={pageChangeHandler}
          />
        </Box>
      )}
    </Card>
  )
}

export default Vins
